// Utilities
import { TraceLevel } from "@ms/uno-constants/lib/local/configuration/ISettings";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
/**
 * Utility to create a proxy for a dynamically loaded component (like an action creator or worker)
 * This is used for calling methods on the component
 *
 * Example:
 *
 * interface ISampleComponent {
 *     doSomething(): void;
 * }
 *
 * Dynamic usage:
 * const component = await loader();
 * component.doSomething();
 *
 * Proxy:
 * const proxy: IComponent = {
 *     doSomething: async () => {
 *         const component = await loader();
 *         component.doSomething();
 *     }
 * }
 *
 *
 * Proxy usage (More concise):
 * proxy.doSomething();
 *
 * IMPORTANT: Absorbing the promise inside the proxy ensures that the caller can call it synchronously.
 * And given that the inner promise is never returned, the caller cannot forcefully await on it either.
 * This ensures that subsequent operations that don't (and should never) depend on the component's result
 * are also executed immediately ensuring optimal performance.
 *
 * @param name Name of the component
 * @param loader Loader that loads the component async (It optionally allows synchronous loading for completeness)
 * @param loggers Loggers
 */
export function createComponentProxy(name, loader, loggers) {
    return new Proxy({}, {
        get(_, methodName) {
            if (typeof methodName === "string") {
                // Special case for pexui handling: subscribeToStore should return undefined
                // This check is needed as if proxy is passed to pws SmartComponent as a prop, it tries to subscribe to it like a store because of the generic get and below conditions in code
                // https://office.visualstudio.com/PathWM/_git/ProjectWebShared?path=/src/controls/base/SmartComponent.ts&version=GBmaster&_a=contents&line=140&lineStyle=plain&lineEnd=142&lineStartColumn=13&lineEndColumn=14
                // This issue was observed for the perfCollection Action creator proxy that is passed as part of pexui instantiation
                //  Also note that we can fix this generically without hardcoding the method name but it would require changes in pws which is in maintenance mode and soon to be retired and we want to avoid making any changes to it currently
                if (methodName === "subscribeToStore") {
                    return undefined;
                }
                // IMPORTANT: The proxy does not return anything to ensure callers cannot depend upon the result
                return (...args) => {
                    Promise.resolve(loader())
                        .then((component) => component[methodName].bind(component).apply(component, args))
                        .catch((error) => {
                        loggers.traceLogger.logTrace(0x1e2401c5 /* tag_4jahf */, TraceLevel.Error, `[Component=${name}][Method=${methodName}][Error=${ErrorUtilities.getMessage(error)}]`);
                    });
                };
            }
        },
    });
}
